import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/client/components/ui/form";
import { Input } from "@/client/components/ui/input";
import { authApi } from "@/client/features/auth/auth-api";
import { useAuthStore } from "@/client/features/auth/auth-store";
import { ApiError } from "@/client/lib/api";
import { InvalidTokenCard, TokenCheckingCard } from "@/client/pages/auth/invalid-token-card";
import { useTokenStatus } from "@/client/pages/auth/use-token-status";
import { PASSWORD_POLICY_HINT, passwordSchema } from "@/shared/password";

const schema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type Values = z.infer<typeof schema>;

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const status = useAuthStore((s) => s.status);
  const logout = useAuthStore((s) => s.logout);
  const tokenStatus = useTokenStatus(token, authApi.checkResetToken);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  if (tokenStatus === "checking") {
    return <TokenCheckingCard />;
  }

  if (!token || tokenStatus === "invalid") {
    return (
      <InvalidTokenCard
        title="Reset link invalid"
        description="This password reset link is invalid or has expired. Request a new one from the sign-in page."
      />
    );
  }

  const onSubmit = async (values: Values) => {
    try {
      await authApi.resetPassword({ token, password: values.password });
      // A reset revokes every session for the account server-side. If the
      // visitor was signed in (now possible since AuthLayout lets token links
      // render), clear the stale client auth state and cookie - otherwise the
      // app would treat them as authenticated and bounce them into a protected
      // shell backed by a dead cookie instead of the login form.
      if (status === "authenticated") {
        await logout();
      }
      toast.success("Password updated. You can sign in now.");
      navigate("/login", { replace: true });
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Unable to reset password. Please try again.";
      toast.error(message);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Choose a new password</CardTitle>
        <CardDescription>Enter a new password for your account.</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New password</FormLabel>
                  <FormControl>
                    <Input type="password" autoComplete="new-password" {...field} />
                  </FormControl>
                  <p className="text-muted-foreground text-xs">{PASSWORD_POLICY_HINT}</p>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm password</FormLabel>
                  <FormControl>
                    <Input type="password" autoComplete="new-password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? "Saving…" : "Update password"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
